import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import CounselorLayout from "@/components/CounselorLayout";
import RecordSection from "@/components/RecordSection";
import GeneralInfo from "@/components/sections/GeneralInfo";
import SemesterPlan from "@/components/sections/SemesterPlan";
import Backup from "@/components/sections/Backup";
import MonthlyReport from "@/components/sections/MonthlyReport";
import GuidanceCommittee from "@/components/GuidanceCommittee";
import StudentCouncil from "@/components/StudentCouncil";
import { SECTIONS } from "@/lib/sectionConfigs";
import CounselorNotices from "@/components/CounselorNotices";
import PrincipalEvaluation from "@/components/PrincipalEvaluation";
import api from "@/lib/api";
import MessageCenter from "@/components/MessageCenter";
import StudentRecords from "@/components/StudentRecords";
import StudentFollowUp from "@/components/StudentFollowUp";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, Calendar as CalIcon, User as UserIcon, Target, MessagesSquare,
} from "lucide-react";

const STAT_CARDS = [
  { key: "individual_session", label: "الجلسات الفردية", icon: UserIcon, color: "#C97052" },
  { key: "group_session", label: "الجلسات الجماعية", icon: Users, color: "#3E6A56" },
  { key: "program", label: "البرامج المنفذة", icon: Target, color: "#DDA74A" },
  { key: "parent_contact", label: "أولياء الأمور", icon: MessagesSquare, color: "#3E6A56" },
  { key: "daily_incident", label: "المواقف اليومية", icon: CalIcon, color: "#C97052" },
];

function Dashboard({ counselorId, viewingUser }) {
  const [stats, setStats] = useState({});

  useEffect(() => {
    const params = counselorId ? { counselor_id: counselorId } : {};
    api.get("/stats", { params }).then((r) => setStats(r.data)).catch(() => {});
  }, [counselorId]);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-sm text-[#3E6A56] font-medium mb-2">لوحة المتابعة</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
          مرحباً، {viewingUser?.name || "مرشد"} 👋
        </h1>
        <p className="text-[#4A554E] mt-2">نظرة سريعة على أعمالك الإرشادية.</p>
      </div>

      {!counselorId && <CounselorNotices />}
      <PrincipalEvaluation counselorId={counselorId} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {STAT_CARDS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="bg-white rounded-2xl border border-[#e6e2d8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:-translate-y-0.5 transition">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}15`, color: s.color }}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-3xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>{stats[s.key] || 0}</div>
              <div className="text-xs text-[#4A554E] mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-gradient-to-tr from-[#3E6A56] to-[#2D4F40] text-white rounded-2xl p-8">
        <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "IBM Plex Sans Arabic" }}>ابدأ من هنا</h3>
        <p className="text-white/80 max-w-xl">
          أضف بياناتك عبر القائمة الجانبية: الحالات، الجلسات، البرامج، أولياء الأمور، الغياب، والتقارير الشهرية.
          كل ما تُدخله يُحفظ لك في السحابة، وخصوصيتك مضمونة تماماً.
        </p>
      </div>
    </div>
  );
}

function SectionRouter({ readOnly = false, counselorId = null, viewingUser = null }) {
  const { section } = useParams();
  const { user } = useAuth();
  const canViewCaseStudy = user?.role === "counselor"
    || (user?.role === "admin" && user?.can_evaluate_counselors === true);
  if (section === "info-form" && !canViewCaseStudy) {
    return <Navigate to={readOnly ? "/supervisor" : "/counselor"} replace />;
  }
  if (section === "general") return <GeneralInfo readOnly={readOnly} counselorId={counselorId} counselor={viewingUser} />;
  if (section === "semester-plan") return <SemesterPlan readOnly={readOnly} counselorId={counselorId} counselorName={viewingUser?.name} counselorGender={viewingUser?.gender} schoolKey={viewingUser?.school_key} schoolName={viewingUser?.school_name} />;
  if (section === "student-records") return <StudentRecords readOnly={readOnly} counselorId={counselorId} schoolKey={viewingUser?.school_key} schoolName={viewingUser?.school_name} counselorGender={viewingUser?.gender} />;
  if (section === "student-followup") return readOnly ? <Navigate to="../general" replace /> : <StudentFollowUp />;
  if (section === "backup") return <Backup />;
  if (section === "communication") return <MessageCenter />;
  if (section === "monthly-report") return <MonthlyReport readOnly={readOnly} counselorId={counselorId} counselorName={viewingUser?.name} counselorGender={viewingUser?.gender} schoolKey={viewingUser?.school_key} schoolName={viewingUser?.school_name} />;
  if (section === "guidance-committee") return <GuidanceCommittee readOnly={readOnly} counselorId={counselorId} counselor={viewingUser} />;
  if (section === "student-council") return <StudentCouncil readOnly={readOnly} counselorId={counselorId} counselor={viewingUser} />;
  const config = SECTIONS[section === "tardy" ? "absence" : section];
  if (!config) return <Navigate to="../general" replace />;
  return <RecordSection key={config.key} config={config} readOnly={readOnly} counselorId={counselorId} counselorName={viewingUser?.name || ""} counselorGender={viewingUser?.gender} schoolKey={viewingUser?.school_key} schoolName={viewingUser?.school_name} />;
}

export default function CounselorDashboard() {
  const { user } = useAuth();
  return (
    <CounselorLayout basePath="/counselor">
      <Routes>
        <Route index element={<Dashboard viewingUser={user} />} />
        <Route path=":section" element={<SectionRouter viewingUser={user} />} />
      </Routes>
    </CounselorLayout>
  );
}

// exported for supervisor drill-down
export function CounselorReadOnlyView({ counselor }) {
  return (
    <CounselorLayout basePath={`/supervisor/counselor/${counselor.id}`} backLink="/supervisor">
      <Routes>
        <Route index element={<Dashboard counselorId={counselor.id} viewingUser={counselor} />} />
        <Route path=":section" element={<SectionRouter readOnly counselorId={counselor.id} viewingUser={counselor} />} />
      </Routes>
    </CounselorLayout>
  );
}
